import type { OverviewResponse } from "contracts/api/dashboard/responses";
import type { Issue } from "contracts/api/issue/responses";
import { BLOCK_TYPE } from "contracts/enums/metrics";
import { For, Show } from "solid-js";
import { BlockViewer } from "../admin/BlockEditor";
import { presentEvidence } from "./evidencePresentation";
import { resolveIssuePages } from "./issuePageResolution";
import { issueBlocksForSite, rawClientIssueText } from "./issueText";

export function IssueDetailDisclosures(props: {
  data: OverviewResponse;
  issue: Issue;
}) {
  const issueEvidence = () =>
    presentEvidence(
      issueBlocksForSite(props.data, props.issue).flatMap((block) =>
        block.type === BLOCK_TYPE.EVIDENCE ? [{ value: block.body }] : [],
      ),
    );
  const pageEvidenceAvailable = () =>
    resolveIssuePages(props.data, props.issue).some(
      ({ link, page }) =>
        page !== null && Boolean(link.evidenceSnippet || link.locator),
    );
  const technicalBlocks = () =>
    issueBlocksForSite(props.data, props.issue).filter(
      (block) =>
        block.type !== BLOCK_TYPE.NOTE &&
        block.type !== BLOCK_TYPE.RECOMMENDATION &&
        block.type !== BLOCK_TYPE.EVIDENCE,
    );
  const rawGeneratedText = () => rawClientIssueText(props.data, props.issue);
  const hasIssueDetail = () =>
    issueEvidence().readable.length > 0 ||
    issueEvidence().technical.length > 0 ||
    technicalBlocks().length > 0;
  return (
    <>
      <Show when={issueEvidence().readable.length > 0}>
        <section class="scan-panel">
          <details class="scan-disclosure scan-issue-disclosure">
            <summary>
              Issue evidence ({issueEvidence().readable.length})
            </summary>
            <div class="scan-disclosure-body">
              <ul class="scan-evidence-list scan-evidence-full">
                <For each={issueEvidence().readable}>
                  {(item) => <li>{item.value}</li>}
                </For>
              </ul>
            </div>
          </details>
        </section>
      </Show>

      <Show
        when={
          issueEvidence().technical.length > 0 ||
          technicalBlocks().length > 0 ||
          rawGeneratedText().length > 0
        }
      >
        <section class="scan-panel">
          <details class="scan-disclosure scan-issue-disclosure">
            <summary>
              {issueEvidence().technical.length > 0
                ? `Technical evidence (${issueEvidence().technical.length})`
                : "Technical detail"}
            </summary>
            <div class="scan-disclosure-body">
              <Show when={issueEvidence().technical.length > 0}>
                <ul class="scan-evidence-list mono">
                  <For each={issueEvidence().technical}>
                    {(item) => <li>{item.value}</li>}
                  </For>
                </ul>
              </Show>
              <Show when={technicalBlocks().length > 0}>
                <BlockViewer blocks={technicalBlocks()} />
              </Show>
              <Show when={rawGeneratedText().length > 0}>
                <dl class="scan-facts scan-raw-generated-copy">
                  <For each={rawGeneratedText()}>
                    {(item) => (
                      <div class="scan-fact">
                        <dt>{item.label}</dt>
                        <dd>{item.value}</dd>
                      </div>
                    )}
                  </For>
                </dl>
              </Show>
            </div>
          </details>
        </section>
      </Show>

      <Show when={!hasIssueDetail() && !pageEvidenceAvailable()}>
        <section class="scan-panel">
          <p class="scan-muted">
            No stored evidence is available for this issue.
          </p>
        </section>
      </Show>
    </>
  );
}
